import React, { useState, useEffect } from 'react';
import { FiArrowRight, FiCalendar, FiCheckCircle, FiClock } from 'react-icons/fi';
import moment from 'moment';
import { useClient } from 'jsonapi-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { TypewriterText } from '@/components/custom/typewriter-text';
import { useSession } from '@/hooks/useSession';
import { HeroLevelCard } from '@/pages/dashboard/home/hero-level-card';
import simuladoIcon from '@/assets/images/infantil/simulado.png';
import trophy3d from '@/assets/images/icons/trophy-3d.png';
import tiaInstrucoes from '@/assets/images/tia/TIA-INSTRUCOES.png';
import tiaJoinha from '@/assets/images/tia/TIA-JOINHA.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Conquista creditada pelo AchievementService quando o aluno finaliza um
// simulado aberto (categoria mérito).
const SIMULADO_ACHIEVEMENT = 'Mestre do Simulado';

// Tela "Sobre o simulado" lúdica (aluno + tema infantil), em três colunas:
// à esquerda a T.I.A com dois balões de fala (descrição + orientações); no
// centro o conteúdo do simulado (ilustração 3D, infos e CTA); à direita a
// pontuação de Conquistas ao finalizar. A distribuição oficial das matérias
// não aparece para o aluno. A variante corporativa continua em
// mock-exams-about.jsx.
export function PlayfulMockExamsAbout({ exam, courseName, onStart }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [achievement, setAchievement] = useState(null);
  const client = useClient();
  const sessionData = useSession();
  const userId = sessionData?.session?.user?.id;

  useEffect(() => {
    if (!userId) return;
    const getAchievement = async () => {
      const { data, error } = await client.fetch(`/user_achievements?user_id=${userId}`);
      if (error) return;
      const found = data?.find(ua => ua.achievement?.title === SIMULADO_ACHIEVEMENT);
      if (found) setAchievement(found.achievement);
    };
    getAchievement();
  }, [userId]);

  const achievementPoints = achievement?.['base-score'] ?? achievement?.base_score;

  const questionCount = exam?.['question-count'] ?? 0;
  const isOpen = moment().isBetween(exam?.['published-at'], exam?.['finished-at'], undefined, true);

  const [h = '0', m = '0'] = (exam?.['resolution-time'] || '').split(':');
  const hours = parseInt(h, 10) || 0;
  const minutes = parseInt(m, 10) || 0;
  const durationLabel = hours >= 1 ? `${hours} ${hours > 1 ? 'horas' : 'hora'}` : `${minutes} minutos`;

  const formatMoment = date => moment(date).format('DD/MM [às] HH[h]mm');

  const infoRows = [
    exam?.['published-at'] &&
      exam?.['finished-at'] && {
        icon: FiCalendar,
        iconBg: 'var(--color-bg-lilac)',
        iconColor: 'var(--color-secondary)',
        label: 'Período',
        value: `De ${formatMoment(exam['published-at'])} até ${formatMoment(exam['finished-at'])}`
      },
    {
      icon: FiClock,
      iconBg: 'var(--color-bg-teal-soft)',
      iconColor: 'var(--color-primary)',
      label: 'Duração',
      value: `${durationLabel} de prova`
    },
    {
      icon: FiCheckCircle,
      iconBg: 'var(--color-bg-mission-soft)',
      iconColor: 'var(--color-mission-medium)',
      label: 'Questões',
      value: `${questionCount} ${questionCount === 1 ? 'questão' : 'questões'}`
    }
  ].filter(Boolean);

  return (
    <>
      <div className="mt-6 grid items-stretch gap-6 lg:grid-cols-[340px_1fr_280px]">
        {/* Coluna da T.I.A: mascote + balões de descrição e orientações. */}
        <aside
          className="flex flex-col items-center rounded-[24px] p-6 shadow-card"
          style={{ background: 'var(--color-bg-lilac)' }}
        >
          <img
            src={tiaInstrucoes}
            alt="T.I.A"
            className="pointer-events-none h-[220px] w-auto object-contain"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} both` }}
          />

          <div
            className="mt-4 w-full rounded-[16px] rounded-tl-none bg-white p-4 shadow-card"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} 150ms both` }}
          >
            <span
              className="block text-xs font-extrabold"
              style={{ color: 'var(--color-secondary-dark)' }}
            >
              T.I.A
            </span>
            <p
              className="mb-0 text-sm font-bold leading-relaxed"
              style={{ color: '#222731' }}
            >
              <TypewriterText text={exam?.description || 'Chegou a hora de treinar! Quando estiver pronto, é só começar. 📝'} />
            </p>
          </div>

          <div
            className="mt-3 w-full rounded-[16px] rounded-tl-none bg-white p-4 shadow-card"
            style={{ animation: `trilha-in 380ms ${EASE_OUT} 700ms both` }}
          >
            <p
              className="mb-0 text-sm font-bold leading-relaxed"
              style={{ color: '#222731' }}
            >
              Leia cada questão com calma e sem pressa. Já bebeu água? Foi ao banheiro? Então respira fundo… e boa prova! 💧✨
            </p>
          </div>
        </aside>

        {/* Coluna do simulado: ilustração 3D, título, infos e CTA. */}
        <section
          className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-8 shadow-card"
          style={{ animation: `trilha-in 380ms ${EASE_OUT} 150ms both` }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
            style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
          />

          <div className="relative flex flex-wrap items-center gap-5">
            <img
              src={simuladoIcon}
              alt=""
              aria-hidden="true"
              className="h-24 w-24 shrink-0 object-contain drop-shadow-sm"
            />
            <div className="min-w-[200px] flex-1">
              <h1
                className="mb-2 text-2xl font-extrabold leading-snug"
                style={{ color: '#222731' }}
              >
                {exam?.title}
              </h1>
              {courseName && (
                <Badge
                  variant="info-soft"
                  className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]"
                >
                  {courseName}
                </Badge>
              )}
            </div>
          </div>

          <div className="relative mt-6 flex flex-col gap-3">
            {infoRows.map(({ icon: Icon, iconBg, iconColor, label, value }, i) => (
              <div
                key={label}
                className="flex items-center gap-4 rounded-[16px] bg-white p-4 shadow-card"
                style={{ animation: `trilha-in 380ms ${EASE_OUT} ${250 + i * 70}ms both` }}
              >
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                  style={{ background: iconBg }}
                >
                  <Icon
                    className="h-5 w-5"
                    style={{ color: iconColor }}
                  />
                </span>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">{label}</span>
                  <span
                    className="text-sm font-extrabold"
                    style={{ color: '#222731' }}
                  >
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="relative mt-auto pt-8">
            {isOpen ? (
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                className="group/btn relative w-full overflow-hidden rounded-full py-4 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
                style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
                />
                <span className="relative inline-flex items-center justify-center gap-2">
                  Começar simulado
                  <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
                </span>
              </button>
            ) : (
              <button
                type="button"
                disabled
                className="w-full cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 py-4 text-sm font-bold text-neutral-400"
              >
                Fora do período
              </button>
            )}
          </div>
        </section>

        {/* Coluna de Conquistas: nível atual + pontuação ao finalizar. */}
        <div className="flex flex-col gap-4">
          <div style={{ animation: `trilha-in 380ms ${EASE_OUT} 300ms both` }}>
            <HeroLevelCard />
          </div>

          <aside
            className="flex flex-1 flex-col items-center justify-center rounded-[24px] border border-neutral-100 p-6 text-center shadow-card"
            style={{ background: 'var(--color-bg-amber-soft)', animation: `trilha-in 380ms ${EASE_OUT} 370ms both` }}
          >
            <img
              src={trophy3d}
              alt=""
              aria-hidden="true"
              className="h-20 w-20 object-contain drop-shadow-sm"
            />
            <span
              className="mt-3 text-xs font-bold uppercase tracking-wide"
              style={{ color: 'var(--color-mission-text)' }}
            >
              Conquistas
            </span>

            {achievementPoints ? (
              <span
                className="mt-1 text-3xl font-extrabold"
                style={{ color: '#222731' }}
              >
                +{achievementPoints} pontos
              </span>
            ) : (
              <span
                className="mt-1 text-2xl font-extrabold"
                style={{ color: '#222731' }}
              >
                Ganhe pontos
              </span>
            )}

            <p className="mb-0 mt-3 text-sm font-semibold leading-relaxed text-neutral-500">
              Termine o simulado e desbloqueie a conquista <strong style={{ color: 'var(--color-mission-text)' }}>{achievement?.title || SIMULADO_ACHIEVEMENT}</strong>. Cada ponto te deixa mais perto do próximo nível! 🏆
            </p>
          </aside>
        </div>
      </div>

      {/* Confirmação amigável antes de iniciar. */}
      <Dialog
        open={showConfirm}
        onOpenChange={setShowConfirm}
      >
        <DialogContent
          size="sm"
          hideCloseButton
          className="w-[400px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
        >
          <div
            className="flex justify-center rounded-t-[24px] pt-6"
            style={{ background: 'var(--color-bg-lilac)' }}
          >
            <img
              src={tiaJoinha}
              alt=""
              aria-hidden="true"
              className="h-32 w-auto object-contain object-bottom"
            />
          </div>

          <div className="flex flex-col items-center px-6 pb-6 pt-5 text-center">
            <DialogTitle
              className="mb-2 text-lg font-extrabold"
              style={{ color: '#222731' }}
            >
              Tudo pronto pra começar?
            </DialogTitle>
            <DialogDescription className="mb-6 text-sm font-semibold leading-relaxed text-neutral-500">
              São {questionCount} {questionCount === 1 ? 'questão' : 'questões'} e você tem {durationLabel} pra responder. Depois de começar, o tempo não para, combinado? 😉
            </DialogDescription>

            <button
              type="button"
              autoFocus
              onClick={() => {
                setShowConfirm(false);
                onStart();
              }}
              className="relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
              style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
              />
              <span className="relative">Vamos lá! 🚀</span>
            </button>

            <button
              type="button"
              onClick={() => setShowConfirm(false)}
              className="mt-3 w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
            >
              Ainda não
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
